import { useState } from "react";
import type { Snapshot } from "../types";

type InboxItem = Snapshot["inbox"][number];
type TodoItem = Snapshot["frankie_todos"][number];

type StripItem =
  | { kind: "inbox"; id: string; lane: string; label: string }
  | { kind: "todo"; id: string; lane: string; label: string };

export function FounderInboxStrip({
  inbox,
  todos,
}: {
  inbox: InboxItem[];
  todos: TodoItem[];
}) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const dismiss = (id: string) =>
    setDismissed((prev) => new Set([...prev, id]));

  const items: StripItem[] = [
    ...inbox
      .filter((i) => i.severity === "urgent" && !dismissed.has(`inbox-${i.id}`))
      .map((i): StripItem => ({
        kind: "inbox",
        id: `inbox-${i.id}`,
        lane: i.lane,
        label: i.summary,
      })),
    ...todos
      .filter((t) => t.urgent && !dismissed.has(`todo-${t.id}`))
      .map((t): StripItem => ({
        kind: "todo",
        id: `todo-${t.id}`,
        lane: t.lane,
        label: t.task,
      })),
  ];

  if (items.length === 0) return null;

  const visible = items.slice(0, 3);
  const overflow = items.length - visible.length;

  // Capitalise first letter of lane name for display
  const capLane = (lane: string) =>
    lane.charAt(0).toUpperCase() + lane.slice(1);

  return (
    <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-950/80 border-b border-amber-600/60 text-amber-200 text-xs overflow-x-auto scroll-thin shrink-0">
      <span className="font-bold text-amber-400 shrink-0">⚠ URGENT</span>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {visible.map((item) => (
          <div
            key={item.id}
            className="flex items-center gap-1 bg-amber-900/50 border border-amber-700/50 rounded px-2 py-0.5 shrink-0 max-w-[360px]"
          >
            <span className="font-semibold text-amber-300 shrink-0">
              {capLane(item.lane)}:
            </span>
            <span className="truncate text-amber-100">{item.label}</span>
            <button
              onClick={() => dismiss(item.id)}
              className="ml-1 text-amber-500 hover:text-white shrink-0 leading-none"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
        {overflow > 0 && (
          <span className="text-amber-500 shrink-0">+{overflow} more</span>
        )}
      </div>
    </div>
  );
}
